import React, { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";

import { Box, Stack, ToggleButtonGroup, ToggleButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Dashboard } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";


import { ActivationAudienceCard, AddAudienceCard, ErrorPanel, HelpBox } from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { InsightLayout } from "@/components/layouts";
import { CompareView } from "@/components/molecules";
import { getInsight } from "@/api";
import { useCompareContext, useDialogs } from "@/context";
import { Audience } from "@/types";
import { useAudiences } from "@/hooks";
import { compareModeOptions, getSegmentPaletteColor } from "@/utils";


const ComparePage: React.FC = () => {
  const { state, dispatch } = useCompareContext();
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const { openDialog, closeDialog } = useDialogs();
  const { state: navState } = useLocation();

  const { audiences, loading: audiencesLoading } = useAudiences("build");
  const [availableAudiences, setAvailableAudiences] = useState<Audience[]>([]);
  const maxAudiences = 2;

  const [compareMode, setCompareMode] = useState<string | null>(null);

  // Create a reference to hold the currently processing audiences
  const processingRef = useRef<Set<string>>(new Set());

  const compareTabOption = {
    name: "comparison",
    label: "Compare",
    icon: <Dashboard fontSize="small" />,
    permission: "",
    description: "",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: false,
    showDownload: false,
  }

  useEffect(() => {

    if (navState?.insight && navState?.audience) {

      // User has navigated here from insight page, add both to state
      dispatch({
        type: "ADD_TO_COMPARE_WITH_INSIGHT",
        payload: {
          audience: navState.audience,
          insight: {
            insight: navState.insight,
            audience_id: navState.audience.audience_id,
            loading: false
          }
        },
      });
    } else if (navState?.audience) {
      // User has navigated here from audience page, add audience to state
      dispatch({ type: "ADD_TO_COMPARE", payload: { audience: navState.audience } });
    }

    return () => {
      // Reset the state when leaving the page
      dispatch({type: "CLEAR_COMPARE"})
    }
  }, [dispatch])

  useEffect(() => {
    audiences && setAvailableAudiences(
      audiences.filter(
        (x) => !state.comparisonAudiences.some(a => a.audience_id === x.audience_id)
      )
    )
  }, [state.comparisonAudiences, audiences]);


  useEffect(() => {

    let cancelled = false;

    const getInsights = async (audienceId: string, index: number) => {

      // Prevent duplicate requests for the same audience
      if (processingRef.current.has(audienceId)) return;
      processingRef.current.add(audienceId);

      try {

        // If audiences are new, but insight exists, set the insight to loading
        if (state.comparisonInsights[index].audience_id !== audienceId) {
          dispatch({
            type: "INITIALISE_COMPARE_INSIGHT",
            payload: { audience_id: audienceId, index }
          })
        }

        // Get the insight
          const accessToken = await getAccessTokenSilently();
          const client = process.env.REACT_APP_CLIENT || 'core';
          const { data, error } = await getInsight(
            accessToken,
            audienceId,
            false,
            client
          );
        if (cancelled) return;

        if (data) {

          // Update the state with the insight
          dispatch({
            type: "ADD_COMPARE_INSIGHT",
            payload: {
              insight: {
                audience_id: audienceId,
                loading: false,
                insight: data.insight
              },
              index
            }
          })
        }

        if (error) {
          dispatch({type: "SET_COMPARE_ERROR", payload: error.message });
        }

      } catch (e) {
        if (cancelled) return;
        dispatch({
          type: "SET_COMPARE_ERROR",
          payload: "Unable to process this comparison. Please refresh the page and try again"
        });

      } finally {
        processingRef.current.delete(audienceId)
      }
    }

    // Process all audiences asynchronously
    Promise.all(state.comparisonAudiences.map((audience, idx) => getInsights(audience.audience_id, idx)));

    return () => {
      cancelled = true;
    }

  }, [state.comparisonAudiences]);

  const handleAddAudiences = (audiences: Audience[]) => {
    // Reset the error state
    dispatch({type: "SET_COMPARE_ERROR", payload: null});

    // Add the audiences to the context
    audiences.forEach((audience) => {
      dispatch({
        type: "ADD_TO_COMPARE",
        payload: { audience }
      });
    });

    // Close the dialog
    closeDialog();
  }

  const handleRemoveAudience = (audience: Audience) => {
    // Reset the error state
    dispatch({type: "SET_COMPARE_ERROR", payload: null});

    // Remove the selected audience from the context
    dispatch({
      type: "REMOVE_FROM_COMPARE",
      payload: audience
    })
  };

  const handleOpenSelectDialog = () => {
    openDialog({
      type: 'selectAudience',
      props: {
        audiences: availableAudiences,
        onConfirm: handleAddAudiences,
        maxAudiences: maxAudiences - state.comparisonAudiences.length,
        message: `Select the audiences you wish to compare. You can select a maximum of ${maxAudiences}.`
      },
    });
  };

  const handleCompareModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: string | null
  ) => {
    setCompareMode(value);
  };

  const compareModeToggle = (
    <Stack>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="flex-end"
        mr={0.5}
      >
        <Typography variant="caption" sx={{ mr: 1 }}>
          Mode:
        </Typography>
        <Typography variant="caption" color="secondary">
          {compareMode ?? 'All'}
        </Typography>
      </Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={compareMode}
        onChange={handleCompareModeChange}
      >
        {compareModeOptions.map((option, index) => (
          <HelpBox
            key={`compare-toggle-${index}`}
            title={option.description}
          >
            <ToggleButton value={option.value}>
              {option.icon}
            </ToggleButton>
          </HelpBox>
        ))}
      </ToggleButtonGroup>
    </Stack>
  )

  return (
    <PageContainer>
      <Grid container spacing={2}>

        <Grid size={12} container spacing={1}>
          {state.error && <ErrorPanel comparisonError={state.error !== null} />}
        </Grid>

        <Grid size={12}>
          <Typography variant="h6" gutterBottom>Audiences</Typography>
          <Typography variant="body2" color="textSecondary">
            Select the audiences you want to compare
          </Typography>
        </Grid>

        <Grid container size={12} spacing={1}>
          {state.comparisonAudiences.map((audience, idx) => (
            <Grid key={idx}>
              <ActivationAudienceCard
                audience={audience}
                onSelect={handleRemoveAudience}
                disabled={false}
                color={getSegmentPaletteColor(theme, idx, maxAudiences)}
              />
            </Grid>
          ))}
          {state.comparisonAudiences.length < maxAudiences && (
            <Grid>
              <AddAudienceCard
                onClick={handleOpenSelectDialog}
                title="Add Audience"
              />
            </Grid>
          )}
        </Grid>

        {state.comparisonInsights && state.comparisonInsights.length > 0 && (
          <InsightLayout
            tab={compareTabOption}
            extraSlot={state.comparisonInsights.length === 2 ? compareModeToggle : undefined}
          >
            <CompareView
              audiences={state.comparisonAudiences}
              insights={state.comparisonInsights}
              mode={compareMode}
            />
          </InsightLayout>
        )}

      </Grid>
    </PageContainer>
  );
};

export default ComparePage;
